import React, { FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { useJobSeeker } from '../../context/JobSeekerContext'; // Corrected import path
import { getProfileCompletionStatus } from '../../utils/profileUtils';

const Profile: FC = () => {
  const { user } = useAuth();
  const { jobSeekerDetails } = useJobSeeker(); // Use useJobSeeker hook
  const [profileStatus, setProfileStatus] = useState({ percentage: 20, completed: false });
  const [sectionStatus, setSectionStatus] = useState({
    personalInfo: false,
    education: false,
    skills: false,
    workExperience: false,
    resume: false,
    jobPreferences: false
  });

  const checkSectionStatus = () => {
    if (!user || !jobSeekerDetails) return;

    // Check each section using user and jobSeekerDetails
    const personalInfo = !!(user.firstName && user.lastName && user.mobileNumber);
    const education = !!(jobSeekerDetails.jobSeekerQualificationDetails && jobSeekerDetails.jobSeekerQualificationDetails.length > 0);
    const skills = !!(jobSeekerDetails.jobSeekerSkillDetails && jobSeekerDetails.jobSeekerSkillDetails.length > 0);
    const workExperience = !!(jobSeekerDetails.jobSeekerExperienceDetails?.yearsOfExperience || jobSeekerDetails.jobSeekerExperienceDetails?.isFresher); // Check for existence of yearsOfExperience
    const resume = (jobSeekerDetails.attachments? jobSeekerDetails.attachments.length > 0 : false); // Assuming resumeUrl exists
    const jobPreferences = !!(jobSeekerDetails.jobSeekerPreferredLocationDetails && jobSeekerDetails.jobSeekerPreferredLocationDetails.length > 0); // Check preferred locations

    setSectionStatus({
      personalInfo,
      education,
      workExperience,
      skills,
      resume,
      jobPreferences
    });
  };

  useEffect(() => {
    if (user && jobSeekerDetails) {
      const status = getProfileCompletionStatus(user, jobSeekerDetails);
      setProfileStatus({
        percentage: status.percentage,
        completed: status.completed
      });
      checkSectionStatus();
    }
  }, [user, jobSeekerDetails]);

  const refreshProfileStatus = () => {
    if (user && jobSeekerDetails) {
      const status = getProfileCompletionStatus(user, jobSeekerDetails);
      setProfileStatus({
        percentage: status.percentage,
        completed: status.completed
      });
      checkSectionStatus();
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">User Profile</h1>
            <p className="text-gray-600">Manage your personal information and preferences</p>
          </div>
          <div className="flex space-x-3">
            <Link to="/user/profile/edit">
              <Button variant="outline">Edit Profile</Button>
            </Link>
          
          </div>
        </div>
      </div>

      {profileStatus.percentage != 100 ? 
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Complete Your Profile
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Complete your profile with detailed information to increase your chances of getting hired. Add your resume, work experience, education, and more.</p>
            </div>
          </div>
        </div>
      </div> : 
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
      <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Profile Complete
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>Update your profile with detailed information to increase your chances of getting hired. Keep your resume, work experience, education, and more up to date.</p>
            </div>
          </div>
        </div>
        </div>
      }

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Profile Overview */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  placeholder="first name not provided"
                  value={user?.firstName || ''}
                  disabled={true}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  placeholder="last name not provided"
                  value={user?.lastName || ''}
                  disabled={true}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  placeholder="Email not provided"
                  value={user?.email || ''}
                  disabled={true}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  placeholder="phone number not provided"
                  value={user?.mobileNumber || ''}
                  disabled={true}
                  readOnly
                />
              </div>
            </div>
            {/* <div className="mt-6">
              <Button className="w-full">Update Basic Info</Button>
            </div> */}
          </div>
        </div>

        {/* Profile Completion Status */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Profile Completion Status</h2>
              <button
                onClick={refreshProfileStatus}
                className="text-sm text-primary-lightGreen hover:text-primary-black font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Personal Information</span>
                <span className={`text-sm font-medium ${sectionStatus.personalInfo ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.personalInfo ? '✓ Complete' : '✗ Missing'}
                </span>
              </div>
              {/* <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Professional Summary</span>
                <span className={`text-sm font-medium ${sectionStatus.professionalSummary ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.professionalSummary ? '✓ Complete' : '✗ Missing'}
                </span>
              </div> */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Education Details</span>
                <span className={`text-sm font-medium ${sectionStatus.education ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.education ? '✓ Complete' : '✗ Missing'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Skills</span>
                <span className={`text-sm font-medium ${sectionStatus.skills ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.education ? '✓ Complete' : '✗ Missing'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Work Experience</span>
                <span className={`text-sm font-medium ${sectionStatus.workExperience ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.workExperience ? '✓ Complete' : '✗ Missing'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Resume Upload</span>
                <span className={`text-sm font-medium ${sectionStatus.resume ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.resume ? '✓ Complete' : '✗ Missing'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Job Preferences</span>
                <span className={`text-sm font-medium ${sectionStatus.jobPreferences ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionStatus.jobPreferences ? '✓ Complete' : '✗ Missing'}
                </span>
              </div>
            </div>
            
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen h-3 rounded-full" style={{ width: `${profileStatus.percentage}%` }}></div>
              </div>
              <p className="text-sm text-gray-600">Profile Completion: {profileStatus.percentage}%</p>
            </div>
            

          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
